import React, { FunctionComponent, Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Container, Tooltip } from "@mui/material";
import HeaderBodyTemplate from "../../components/templates/HeaderBodyTemplate";
import { PageHeader } from "../../components/organisms/PageHeader";
import ListTable from "../../components/organisms/ListTable";
import InfoPopOver from "../../components/molecules/InfoPopOver";
import { FiCheck, FiClock, FiX } from "react-icons/fi";
import { BsShieldCheck } from "react-icons/bs";
import CheckExclamation from "../../assets/images/checkExclamation.svg";
import { StyledStack } from "./style";
import FlagWrapper from "../../components/FlagWrapper";
import { getCountries } from "../../usecases/countries/countriesUseCase";
import { BREADCRUMBS, HEADER_TABLE_COLUMNS, ICONS_MAP, LABELS_MAP, PAGE_TITLE, tableData } from "./contants";
import { useGetApprovedFeedstockQuery } from "../../store/ducks/approvedFeedstock";
import { filterArrayByOptions } from "./helper";
const ApprovedFeedstockFilter = lazy(() => import("./components/ApprovedFeedstockFilter"));

type ApprovedFeedstockHeaderProps = {
  onOpenFilter: () => void
}

type ApprovedFeedstockBodyProps = {
  filterOpen: boolean,
  onCloseFilter: () => void
}

const getStatusIcon = (status: string) => {
  return (
    <Tooltip placement="bottom-start" title={LABELS_MAP.get(status)}>
      <span>{ICONS_MAP.get(status)}</span>
    </Tooltip>
  );
};

const replaceStatusWithIcon = (tableData: tableData[]) => {
  return tableData.map((row: tableData) => {
    const {port, origin, product, q1BaGlobal, q1BaReforma, q2RsGlobal} = row;
    const portWithFlag = <FlagWrapper country={origin} label={port} />;
    return [portWithFlag, product, getStatusIcon(q1BaGlobal), getStatusIcon(q1BaReforma), getStatusIcon(q2RsGlobal)];
  });

};

const ApprovedFeedstockHeader: FunctionComponent<ApprovedFeedstockHeaderProps> = ({onOpenFilter}) => {
  return (
    <PageHeader.Root>
      <PageHeader.Breadcrumb breadcrumbs={BREADCRUMBS} />
      <PageHeader.Title title={PAGE_TITLE} />
      <PageHeader.Filter onOpenFilter={onOpenFilter} />
    </PageHeader.Root>
  );
};

const ApprovedFeedstockPopOver: FunctionComponent = () => {
  return (
    <InfoPopOver>
      <StyledStack gap="20px">
        <span><FiCheck color="#78BE20" /> Approved</span>
        <span><img src={CheckExclamation} />Approved with restrictions</span>
        <span><BsShieldCheck color="#78BE20" />Homologated</span>
        <span><FiClock color="#FF8E00" />In homologation</span>
        <span><FiX color="#EE0000" />Not approved</span>  
      </StyledStack>
    </InfoPopOver>
  );
};

const ApprovedFeedstockBody: FunctionComponent<ApprovedFeedstockBodyProps> = ({filterOpen, onCloseFilter}) => {
  const [filterOptions, setFilterOptions] = useState([]);
  const {data: approvedFeedstockData, isLoading } = useGetApprovedFeedstockQuery();

  const filteredData = useMemo(() => {
    let results = !isLoading ? approvedFeedstockData : [];
    results = filterArrayByOptions(results, filterOptions);
    return results || [];
  }, [approvedFeedstockData, filterOptions]);

  const formatDataToTable = (data: tableData[]) => {
    const findStatusOnSystems = (systems: any[], siteName: string, systemName: string) => {
      return systems.find((systemStatus) => {
        const {system} = systemStatus;
        return system.site.name === siteName && system.name === systemName;
      });
    };
    return data.map((row: any) => {
      return {
        port: row.port?.name,
        origin: row.port?.country?.name,
        product: row.product?.name,
        q1BaGlobal: findStatusOnSystems(row.systems, "Q1BA", "UO1")?.status,
        q1BaReforma: findStatusOnSystems(row.systems, "Q1BA", "Reformer")?.status,
        q2RsGlobal: findStatusOnSystems(row.systems, "Q2RS", "UO1")?.status
      };
    });
  };

  const formattedData = formatDataToTable(filteredData);
  const dataWithIcons = replaceStatusWithIcon(formattedData);

  const handleApplyFilter = (options: string[]) => {
    setFilterOptions(options);
    onCloseFilter();
  };

  return (
    <Container sx={{paddingTop: "20px"}}>
      <ListTable columns={HEADER_TABLE_COLUMNS} isLoading={isLoading} list={dataWithIcons} />
      <ApprovedFeedstockPopOver />
      {filterOpen &&
        <Suspense fallback={<div>Loading...</div>}>
          <ApprovedFeedstockFilter isOpen={filterOpen} onApply={handleApplyFilter} onClose={onCloseFilter} />
        </Suspense>
      }
    </Container>
  );
};

export default function ApprovedFeedstock() {
  const [filterOpen, setFilterOpen] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCountries());
  }, []);

  const handleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  return (
    <HeaderBodyTemplate
      header={<ApprovedFeedstockHeader onOpenFilter={handleFilter} />}
      body={<ApprovedFeedstockBody filterOpen={filterOpen} onCloseFilter={handleFilter} />}
    />
  );   
}